// IntegrationsView.jsx — Modal de integrações com apps externos.
//
// Sprint v9.8 — abre TODOS os providers pra conexão via token/PAT.
// 3 status: available (form de token), oauth-soon (em breve), local-only
// (app local sem token — Obsidian/Playwright/Granola).
//
// Generic TokenForm substitui o LinearForm dedicado. Cada provider tem
// docsUrl + tokenLabel customizados no catalog (integrationsCatalog.js).
//
// API:
//   GET    /api/integrations                  → status por provider
//   POST   /api/integrations/:provider        { api_key } → grava + valida
//   POST   /api/integrations/:provider/test   → re-valida
//   DELETE /api/integrations/:provider        → revoga

import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  AlertCircle,
  Check,
  ExternalLink,
  Loader2,
  RefreshCw,
  Rocket,
  Search,
  Trash2,
  X,
  Zap,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { apiFetch } from '../lib/api.js';
import { INTEGRATIONS, INTEGRATION_CATEGORIES } from '../lib/integrationsCatalog.js';

const SPRING = { type: 'spring', stiffness: 320, damping: 28 };

function StatusBadge({ connected }) {
  return (
    <span
      aria-label={connected ? 'Conectado' : 'Não conectado'}
      className={
        'inline-flex h-1.5 w-1.5 shrink-0 rounded-full ' +
        (connected ? 'bg-emerald-400' : 'bg-apolo-textTertiary/40')
      }
      style={
        connected
          ? {
              boxShadow:
                '0 0 0 1px rgba(52,211,153,0.30), 0 0 6px rgba(52,211,153,0.55)',
            }
          : undefined
      }
    />
  );
}

function ProviderLogo({ integration }) {
  const [failed, setFailed] = useState(false);
  if (failed || !integration.iconUrl) {
    return (
      <span
        aria-hidden="true"
        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-apolo-surface3 text-xs font-bold text-apolo-textPrimary"
      >
        {integration.name.charAt(0)}
      </span>
    );
  }
  return (
    <span
      aria-hidden="true"
      className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-apolo-surface3 p-1.5"
    >
      <img
        src={integration.iconUrl}
        alt=""
        width={20}
        height={20}
        loading="lazy"
        onError={() => setFailed(true)}
        className="block h-5 w-5 object-contain"
      />
    </span>
  );
}

// OAuthButton — pros providers com integration.flow === 'oauth' (Google
// Calendar/Gmail/Drive). Click abre popup com Authorize URL do provider,
// callback do Worker fecha popup + posta apolo:oauth-complete. Apolo escuta
// e refresca o status.
function OAuthButton({ integration, connected, onTest, onRevoke, onRefresh }) {
  const [busy, setBusy] = useState(false);
  const [testResult, setTestResult] = useState(null);

  async function startOAuth() {
    setBusy(true);
    setTestResult(null);
    const res = await apiFetch(integration.oauthStartPath || `/api/oauth/google/start?provider=${integration.id}`);
    if (!res.ok || !res.data?.authorize_url) {
      setBusy(false);
      const msg = res.error || 'OAuth indisponível — verifique GOOGLE_CLIENT_ID/SECRET no Worker';
      setTestResult({ ok: false, msg });
      return;
    }
    // Abre popup. Callback do Worker fecha sozinho via window.close().
    const w = 540, h = 680;
    const left = window.screenX + (window.outerWidth - w) / 2;
    const top = window.screenY + (window.outerHeight - h) / 2;
    const popup = window.open(
      res.data.authorize_url,
      'apolo-oauth',
      `width=${w},height=${h},left=${left},top=${top},resizable=yes,scrollbars=yes`,
    );
    if (!popup) {
      setBusy(false);
      setTestResult({ ok: false, msg: 'Popup bloqueado — autorize popups pro Apolo' });
      return;
    }
    // Escuta postMessage do callback
    function handler(e) {
      const data = e?.data;
      if (!data || typeof data !== 'object') return;
      if (data.type === 'apolo:oauth-complete' && data.provider === integration.id) {
        window.removeEventListener('message', handler);
        setBusy(false);
        setTestResult({ ok: true, msg: 'Conectado' });
        onRefresh?.();
      } else if (data.type === 'apolo:oauth-error') {
        window.removeEventListener('message', handler);
        setBusy(false);
        setTestResult({ ok: false, msg: data.error || 'falhou' });
      }
    }
    window.addEventListener('message', handler);
    // Fallback: se popup fechar sem mensagem, libera o busy
    const checkClosed = setInterval(() => {
      if (popup.closed) {
        clearInterval(checkClosed);
        setTimeout(() => {
          setBusy(false);
          onRefresh?.();
        }, 600);
      }
    }, 500);
  }

  async function handleTest() {
    setBusy(true);
    setTestResult(null);
    const res = await onTest(integration.id);
    setBusy(false);
    setTestResult({
      ok: !!res?.ok,
      msg: res?.ok ? (res?.user ? `Conectado como ${res.user}` : 'Conectado') : (res?.error || 'falhou'),
    });
  }

  async function handleRevoke() {
    if (!window.confirm(`Revogar conexão ${integration.name}?`)) return;
    setBusy(true);
    await onRevoke(integration.id);
    setBusy(false);
    setTestResult(null);
  }

  return (
    <div className="mt-3 flex flex-col gap-2 rounded-md border border-apolo-borderSubtle bg-apolo-surface2/40 p-3">
      <div className="flex flex-col gap-1">
        <span className="text-[11px] uppercase tracking-wider text-apolo-textTertiary">
          Autenticação OAuth
        </span>
        <p className="text-[11px] text-apolo-textSecondary">
          Click pra autorizar o Apolo no Google. Você verá a tela padrão do Google
          mostrando os scopes solicitados — após aprovar, retornamos automaticamente.
        </p>
      </div>

      {testResult && (
        <motion.div
          initial={{ opacity: 0, y: -4 }}
          animate={{ opacity: 1, y: 0 }}
          transition={SPRING}
          className={
            'flex items-center gap-1.5 rounded-md px-2 py-1 text-[11px] ' +
            (testResult.ok
              ? 'bg-emerald-500/10 text-emerald-300'
              : 'bg-apolo-error/15 text-apolo-error')
          }
          role="status"
        >
          {testResult.ok ? <Check size={11} aria-hidden="true" /> : <AlertCircle size={11} aria-hidden="true" />}
          {testResult.msg}
        </motion.div>
      )}

      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={startOAuth}
          disabled={busy}
          className="inline-flex items-center gap-1.5 rounded-md bg-apolo-accent px-3 py-1.5 text-xs font-medium text-apolo-bg transition hover:bg-apolo-accentHover disabled:opacity-50"
        >
          {busy ? <Loader2 size={11} className="animate-spin" aria-hidden="true" /> : null}
          {connected ? 'Reautorizar' : 'Conectar com Google'}
        </button>
        {connected && (
          <>
            <button
              type="button"
              onClick={handleTest}
              disabled={busy}
              className="inline-flex items-center gap-1 rounded-md border border-apolo-borderSubtle bg-apolo-surface2 px-3 py-1.5 text-xs text-apolo-textSecondary transition hover:border-apolo-borderStrong hover:text-apolo-textPrimary disabled:opacity-50"
            >
              Testar
            </button>
            <button
              type="button"
              onClick={handleRevoke}
              disabled={busy}
              aria-label="Revogar"
              className="ml-auto inline-flex h-7 w-7 items-center justify-center rounded-md text-apolo-textTertiary transition hover:bg-apolo-error/15 hover:text-apolo-error disabled:opacity-50"
            >
              <Trash2 size={12} aria-hidden="true" />
            </button>
          </>
        )}
      </div>
    </div>
  );
}

// Generic TokenForm — funciona pra QUALQUER provider available.
// Validators específicos (linear/supabase/cloudflare/github) rodam no Worker.
function TokenForm({ integration, connected, onSave, onTest, onRevoke }) {
  const [token, setToken] = useState('');
  const [busy, setBusy] = useState(false);
  const [testResult, setTestResult] = useState(null);

  async function handleSave(e) {
    e.preventDefault();
    if (!token.trim()) return;
    setBusy(true);
    setTestResult(null);
    const res = await onSave(integration.id, token.trim());
    setBusy(false);
    if (res?.ok) {
      setTestResult({ ok: true, msg: res?.data?.user ? `Conectado como ${res.data.user}` : 'Conectado' });
      setToken('');
    } else {
      setTestResult({ ok: false, msg: res?.error || 'falhou' });
    }
  }

  async function handleTest() {
    setBusy(true);
    setTestResult(null);
    const res = await onTest(integration.id);
    setBusy(false);
    setTestResult({
      ok: !!res?.ok,
      msg: res?.ok ? (res?.user ? `Conectado como ${res.user}` : 'Conectado') : (res?.error || 'falhou'),
    });
  }

  async function handleRevoke() {
    if (!window.confirm(`Revogar conexão ${integration.name}? A chave será deletada.`)) return;
    setBusy(true);
    await onRevoke(integration.id);
    setBusy(false);
    setTestResult(null);
  }

  return (
    <form
      onSubmit={handleSave}
      className="mt-3 flex flex-col gap-2 rounded-md border border-apolo-borderSubtle bg-apolo-surface2/40 p-3"
    >
      <label className="flex flex-col gap-1">
        <span className="text-[11px] uppercase tracking-wider text-apolo-textTertiary">
          {integration.tokenLabel || 'API Token'}
        </span>
        <input
          type="password"
          value={token}
          onChange={(e) => setToken(e.target.value)}
          placeholder={integration.tokenPlaceholder || 'cole aqui o token…'}
          autoComplete="off"
          spellCheck={false}
          className="rounded-md border border-apolo-border bg-apolo-surface3 px-2 py-1.5 font-mono text-xs text-apolo-textPrimary placeholder:text-apolo-textTertiary focus:border-apolo-accent focus:outline-none focus-visible:ring-2 focus-visible:ring-apolo-accentRing"
        />
        {integration.docsUrl && (
          <a
            href={integration.docsUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex w-fit items-center gap-1 text-[10px] text-apolo-textTertiary hover:text-apolo-accent"
          >
            {integration.docsLabel || 'Como obter →'}
            <ExternalLink size={9} aria-hidden="true" />
          </a>
        )}
      </label>

      {testResult && (
        <motion.div
          initial={{ opacity: 0, y: -4 }}
          animate={{ opacity: 1, y: 0 }}
          transition={SPRING}
          className={
            'flex items-center gap-1.5 rounded-md px-2 py-1 text-[11px] ' +
            (testResult.ok
              ? 'bg-emerald-500/10 text-emerald-300'
              : 'bg-apolo-error/15 text-apolo-error')
          }
          role="status"
        >
          {testResult.ok ? (
            <Check size={11} aria-hidden="true" />
          ) : (
            <AlertCircle size={11} aria-hidden="true" />
          )}
          {testResult.msg}
        </motion.div>
      )}

      <div className="flex items-center gap-2">
        <button
          type="submit"
          disabled={busy || !token.trim()}
          className="inline-flex items-center gap-1 rounded-md bg-apolo-accent px-3 py-1.5 text-xs font-medium text-apolo-bg transition hover:bg-apolo-accentHover disabled:opacity-50"
        >
          {busy ? <Loader2 size={11} className="animate-spin" aria-hidden="true" /> : null}
          {connected ? 'Atualizar' : 'Conectar'}
        </button>
        {connected && (
          <>
            <button
              type="button"
              onClick={handleTest}
              disabled={busy}
              className="inline-flex items-center gap-1 rounded-md border border-apolo-borderSubtle bg-apolo-surface2 px-3 py-1.5 text-xs text-apolo-textSecondary transition hover:border-apolo-borderStrong hover:text-apolo-textPrimary disabled:opacity-50"
            >
              Testar
            </button>
            <button
              type="button"
              onClick={handleRevoke}
              disabled={busy}
              aria-label="Revogar"
              className="ml-auto inline-flex h-7 w-7 items-center justify-center rounded-md text-apolo-textTertiary transition hover:bg-apolo-error/15 hover:text-apolo-error disabled:opacity-50"
            >
              <Trash2 size={12} aria-hidden="true" />
            </button>
          </>
        )}
      </div>
    </form>
  );
}

function IntegrationCard({ integration, connection, onSave, onTest, onRevoke, onRefresh, forceExpanded = false }) {
  const [localExpanded, setLocalExpanded] = useState(false);
  const expanded = forceExpanded || localExpanded;
  const setExpanded = (v) => setLocalExpanded(typeof v === 'function' ? v(localExpanded) : v);
  const connected = !!connection?.connected;
  const available = integration.status === 'available';
  const oauthSoon = integration.status === 'oauth-soon';
  const localOnly = integration.status === 'local-only';
  const connectedElsewhere = integration.status === 'connected-elsewhere';

  let cta = null;
  if (available) {
    cta = (
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
        className="inline-flex h-7 items-center gap-1 rounded-md border border-apolo-borderSubtle bg-apolo-surface2 px-2 text-[11px] text-apolo-textSecondary transition hover:border-apolo-accent/40 hover:bg-apolo-surface3 hover:text-apolo-textPrimary"
      >
        {connected ? 'Gerenciar' : 'Conectar'}
      </button>
    );
  } else if (connectedElsewhere) {
    cta = (
      <span className="inline-flex items-center gap-1 rounded-md border border-emerald-500/30 bg-emerald-500/10 px-2 py-0.5 text-[9px] uppercase tracking-wider text-emerald-300">
        <Check size={9} aria-hidden="true" /> conectado
      </span>
    );
  } else if (oauthSoon) {
    cta = (
      <span
        title="OAuth flow em desenvolvimento — disponível nas próximas sprints"
        className="rounded-md border border-apolo-borderSubtle/40 bg-apolo-surface2/40 px-2 py-0.5 text-[9px] uppercase tracking-wider text-apolo-textTertiary"
      >
        OAuth em breve
      </span>
    );
  } else if (localOnly) {
    cta = (
      <span
        title={integration.note || 'App local — sem token'}
        className="rounded-md border border-apolo-borderSubtle/40 bg-apolo-surface2/40 px-2 py-0.5 text-[9px] uppercase tracking-wider text-apolo-textTertiary"
      >
        local
      </span>
    );
  }

  const borderClass = connected
    ? 'border-emerald-500/30'
    : connectedElsewhere
      ? 'border-emerald-500/20'
      : available
        ? 'border-apolo-borderSubtle hover:border-apolo-accent/40'
        : 'border-apolo-borderSubtle/40 opacity-70 hover:opacity-100';

  return (
    <article className={'flex flex-col rounded-xl border bg-apolo-surface1 p-3 transition ' + borderClass}>
      <header className="flex items-center gap-2">
        <ProviderLogo integration={integration} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <span className="truncate text-sm font-medium text-apolo-textPrimary">
              {integration.name}
            </span>
            {connected && <StatusBadge connected />}
          </div>
          <p className="truncate text-[10px] text-apolo-textTertiary">{integration.description}</p>
        </div>
        {cta}
      </header>

      <AnimatePresence initial={false}>
        {expanded && available && (
          <motion.div
            key={`${integration.id}-form`}
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ ...SPRING, opacity: { duration: 0.15 } }}
            className="overflow-hidden"
          >
            {integration.flow === 'oauth' ? (
              <OAuthButton
                integration={integration}
                connected={connected}
                onTest={onTest}
                onRevoke={onRevoke}
                onRefresh={onRefresh}
              />
            ) : (
              <TokenForm
                integration={integration}
                connected={connected}
                onSave={onSave}
                onTest={onTest}
                onRevoke={onRevoke}
              />
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {localOnly && integration.note && (
        <p className="mt-2 text-[10px] leading-relaxed text-apolo-textTertiary">
          {integration.note}
        </p>
      )}
    </article>
  );
}

export default function IntegrationsView({ open, onClose }) {
  const [integrations, setIntegrations] = useState({});
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');
  // Sprint v9.9 — Connect All mode: expande TODOS os cards available
  // simultaneamente pra colagem em batch (Pedro tem 10 abas abertas no Dia
  // com os PATs a criar, percorre vertical e cola tudo de uma vez).
  const [connectAllMode, setConnectAllMode] = useState(false);
  // #6 — "Atualizar LLMs": pergunta a cada provider configurado os modelos novos.
  const [modelRefresh, setModelRefresh] = useState({ running: false, msg: '' });

  async function refreshModels() {
    setModelRefresh({ running: true, msg: '' });
    const res = await apiFetch('/api/models/refresh', { method: 'POST' });
    const added = res.ok ? (res.data?.added || 0) : 0;
    setModelRefresh({
      running: false,
      msg: res.ok ? (added ? `+${added} novo${added === 1 ? '' : 's'}` : 'Tudo atualizado') : 'Falhou',
    });
    // notify the model pickers to re-read /api/models/extra
    try { window.dispatchEvent(new CustomEvent('apolo:models-changed')); } catch { /* noop */ }
    setTimeout(() => setModelRefresh((s) => ({ ...s, msg: '' })), 3000);
  }

  async function refresh() {
    setLoading(true);
    const res = await apiFetch('/api/integrations');
    if (res.ok && res.data?.integrations) {
      const map = {};
      for (const it of res.data.integrations) map[it.provider] = it;
      setIntegrations(map);
    }
    setLoading(false);
  }

  useEffect(() => {
    if (open) refresh();
  }, [open]);

  async function saveProvider(provider, apiKey) {
    const res = await apiFetch(`/api/integrations/${encodeURIComponent(provider)}`, {
      method: 'POST',
      body: { api_key: apiKey },
    });
    await refresh();
    return res;
  }

  async function testProvider(provider) {
    const res = await apiFetch(`/api/integrations/${encodeURIComponent(provider)}/test`, { method: 'POST' });
    return res.ok ? { ok: true, user: res.data?.user } : { ok: false, error: res.error };
  }

  async function revokeProvider(provider) {
    await apiFetch(`/api/integrations/${encodeURIComponent(provider)}`, { method: 'DELETE' });
    await refresh();
  }

  // Filtra + busca
  const filtered = useMemo(() => {
    let list = INTEGRATIONS;
    if (activeCategory !== 'all') {
      list = list.filter((i) => i.category === activeCategory);
    }
    if (query.trim()) {
      const q = query.trim().toLowerCase();
      list = list.filter(
        (i) =>
          i.name.toLowerCase().includes(q) ||
          i.description.toLowerCase().includes(q) ||
          i.id.toLowerCase().includes(q),
      );
    }
    return list;
  }, [activeCategory, query]);

  const categoryCounts = useMemo(() => {
    const counts = { all: INTEGRATIONS.length };
    for (const cat of INTEGRATION_CATEGORIES) {
      if (cat.id === 'all') continue;
      counts[cat.id] = INTEGRATIONS.filter((i) => i.category === cat.id).length;
    }
    return counts;
  }, []);

  const availableCount = useMemo(
    () => INTEGRATIONS.filter((i) => i.status === 'available').length,
    [],
  );

  const connectedCount = useMemo(() => {
    let n = 0;
    for (const it of INTEGRATIONS) {
      if (integrations[it.id]?.connected) n++;
    }
    return n;
  }, [integrations]);

  // Connect All mode + abre docs em batch
  function activateConnectAll() {
    setConnectAllMode(true);
    setActiveCategory('all');
    setQuery('');
    // Abre as URLs de criação de token em abas paralelas pros providers
    // top-priority. Browser pode bloquear popups; o user clica em "Permitir"
    // se isso acontecer — primeira chamada serve de trigger user-gesture.
    const TOP = [
      'linear', 'supabase', 'cloudflare', 'notion', 'figma',
      'slack', 'vercel', 'netlify', 'sentry',
    ];
    for (const id of TOP) {
      const it = INTEGRATIONS.find((i) => i.id === id);
      if (it?.docsUrl) {
        try {
          window.open(it.docsUrl, '_blank', 'noopener,noreferrer');
        } catch {
          // popup blocker — Pedro abre manual
        }
      }
    }
  }

  if (!open || typeof document === 'undefined') return null;

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="integrations-title"
      className="fixed inset-0 z-[70] flex items-stretch justify-stretch bg-black/70 lg:items-center lg:justify-center lg:p-4"
    >
      <div className="flex h-[100dvh] max-h-[100dvh] w-full max-w-full flex-col border-0 bg-apolo-surface1 shadow-2xl lg:h-auto lg:max-h-[88vh] lg:max-w-4xl lg:rounded-apolo-2xl lg:border lg:border-apolo-borderSubtle">
        <header
          className="flex shrink-0 items-center gap-2 border-b border-apolo-borderSubtle p-4"
          style={{ paddingTop: 'max(1rem, env(safe-area-inset-top))' }}
        >
          <Zap size={18} className="text-apolo-accent" aria-hidden="true" />
          <h2 id="integrations-title" className="text-sm font-semibold text-apolo-textPrimary">
            Integrações
          </h2>
          <span className="text-xs text-apolo-textTertiary">
            <strong className="text-apolo-textSecondary tabular-nums">{connectedCount}</strong> conectada
            {connectedCount === 1 ? '' : 's'} · {availableCount} disponíveis
          </span>
          <div className="ml-auto flex items-center gap-1.5">
            <button
              type="button"
              onClick={refreshModels}
              disabled={modelRefresh.running}
              title="Busca modelos LLM novos nos providers configurados e adiciona no seletor"
              className="inline-flex h-9 shrink-0 items-center gap-1 rounded-md border border-apolo-borderSubtle bg-apolo-surface2 px-2.5 text-xs text-apolo-textSecondary transition hover:border-apolo-borderStrong hover:text-apolo-textPrimary focus:outline-none focus-visible:ring-2 focus-visible:ring-apolo-accentRing disabled:opacity-60"
            >
              <RefreshCw size={13} className={modelRefresh.running ? 'animate-spin' : ''} aria-hidden="true" />
              {modelRefresh.running ? 'Atualizando…' : (modelRefresh.msg || 'Atualizar LLMs')}
            </button>
            {connectAllMode ? (
              <button
                type="button"
                onClick={() => setConnectAllMode(false)}
                className="inline-flex h-9 items-center gap-1 rounded-md border border-apolo-borderSubtle bg-apolo-surface2 px-3 text-xs text-apolo-textSecondary transition hover:border-apolo-borderStrong hover:text-apolo-textPrimary"
              >
                Modo cards
              </button>
            ) : (
              <button
                type="button"
                onClick={activateConnectAll}
                title="Abre docs de PAT em abas + expande todos os forms pra você colar em sequência"
                className="relative inline-flex h-9 shrink-0 items-center gap-1.5 overflow-hidden rounded-md px-3 text-xs font-medium text-apolo-bg transition focus:outline-none focus-visible:ring-2 focus-visible:ring-apolo-accentRing"
                style={{
                  background: 'var(--apolo-cta-gradient)',
                  boxShadow: 'var(--apolo-cta-shadow)',
                }}
              >
                <Rocket size={12} aria-hidden="true" />
                Conectar tudo
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              aria-label="Fechar"
              className="inline-flex h-11 w-11 items-center justify-center rounded-apolo-md text-apolo-textTertiary transition hover:bg-apolo-surface2 hover:text-apolo-textPrimary focus:outline-none focus-visible:ring-2 focus-visible:ring-apolo-accentRing lg:h-9 lg:w-9"
            >
              <X size={16} aria-hidden="true" />
            </button>
          </div>
        </header>
        {/* Banner do Connect All mode */}
        {connectAllMode && (
          <div className="flex shrink-0 items-start gap-2 border-b border-apolo-accent/30 bg-apolo-accent/10 px-4 py-2 text-[11px] text-apolo-textSecondary">
            <Rocket size={12} className="mt-0.5 shrink-0 text-apolo-accent" aria-hidden="true" />
            <div className="flex-1">
              <strong className="text-apolo-textPrimary">Modo Conectar Tudo:</strong>{' '}
              abri abas das docs dos 9 providers principais. Cria o PAT em cada um, copia, e cola no
              respectivo campo aqui. Cada Conectar valida e salva imediatamente.
            </div>
          </div>
        )}

        <div className="flex shrink-0 flex-col gap-2 border-b border-apolo-borderSubtle px-4 py-2">
          <div className="flex items-center gap-2 rounded-md border border-apolo-borderSubtle bg-apolo-surface2 px-2 focus-within:ring-2 focus-within:ring-apolo-accentRing">
            <Search size={12} className="shrink-0 text-apolo-textTertiary" aria-hidden="true" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Buscar integração…"
              aria-label="Buscar integração"
              className="flex-1 bg-transparent py-1.5 text-xs text-apolo-textPrimary placeholder:text-apolo-textTertiary focus:outline-none"
            />
            {query && (
              <button
                type="button"
                onClick={() => setQuery('')}
                aria-label="Limpar busca"
                className="text-apolo-textTertiary hover:text-apolo-textPrimary"
              >
                <X size={11} aria-hidden="true" />
              </button>
            )}
          </div>
          <nav
            aria-label="Categorias"
            className="flex items-center gap-1 overflow-x-auto"
            style={{ scrollbarWidth: 'none' }}
          >
            {INTEGRATION_CATEGORIES.map((cat) => {
              const isActive = activeCategory === cat.id;
              const count = categoryCounts[cat.id];
              return (
                <button
                  key={cat.id}
                  type="button"
                  onClick={() => setActiveCategory(cat.id)}
                  aria-pressed={isActive}
                  className={
                    'inline-flex shrink-0 items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] font-medium transition ' +
                    (isActive
                      ? 'border-apolo-accent/60 bg-apolo-accent/10 text-apolo-textPrimary'
                      : 'border-apolo-borderSubtle bg-transparent text-apolo-textSecondary hover:border-apolo-borderStrong hover:text-apolo-textPrimary')
                  }
                >
                  {cat.label}
                  <span className="rounded-sm bg-apolo-surface3 px-1 text-[9px] tabular-nums text-apolo-textTertiary">
                    {count}
                  </span>
                </button>
              );
            })}
          </nav>
        </div>

        <div
          className="flex flex-1 flex-col gap-3 overflow-auto p-4"
          style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}
        >
          {loading && (
            <div className="flex items-center justify-center gap-2 py-6 text-xs text-apolo-textTertiary">
              <Loader2 size={12} className="animate-spin" aria-hidden="true" />
              Carregando…
            </div>
          )}

          {!loading && filtered.length === 0 && (
            <div className="flex flex-col items-center justify-center gap-1 py-10 text-center text-xs text-apolo-textTertiary">
              <Search size={16} className="text-apolo-textTertiary/50" aria-hidden="true" />
              Nenhuma integração com esse filtro.
            </div>
          )}

          <div
            className={
              connectAllMode
                ? 'flex flex-col gap-2'
                : 'grid grid-cols-1 gap-2 md:grid-cols-2 xl:grid-cols-3'
            }
          >
            {filtered.map((it) => (
              <IntegrationCard
                key={it.id}
                integration={it}
                connection={integrations[it.id]}
                onSave={saveProvider}
                onTest={testProvider}
                onRevoke={revokeProvider}
                onRefresh={refresh}
                forceExpanded={connectAllMode && it.status === 'available'}
              />
            ))}
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
